import React from "react";
import { useSelector } from "react-redux";
import { useIsLoggedIn } from "@dynamic-labs/sdk-react-core";
import {
  BanIcon,
  CheckIcon,
  HandCoinsIcon,
  HourglassIcon,
  XIcon,
} from "lucide-react";
import {
  GetBattleByIdApiResponse as Battle,
  BattlesType,
  ClaimType,
  Position,
  ResponseBattleType,
} from "@dripit/types";

import {
  Button,
  ButtonProps,
  ClaimButton,
  WithTooltip,
} from "@/components/main";
import { cn } from "@/helpers/tailwind";
import { BattleDetails, BattleStatus, UserBattleStatus } from "@/types";
import isNil from "lodash.isnil";
import isEmpty from "lodash.isempty";
import { getBattleStatus } from "@/helpers/battleHelpers";
import { delay } from "@/helpers";
import { RootState } from "@/store";

interface CtaProps extends ButtonProps {
  battle?: Partial<Battle> | Partial<ResponseBattleType>;
  minimal?: boolean;
  minimalMobile?: boolean;
  status: UserBattleStatus;
  position?: number;
  artistId: number;
  winnerId?: Battle["winner_id"];
  claims?: BattleDetails["claims"];
  refetchBattles?: () => void;
}

export const Cta: React.FC<CtaProps> = ({
  battle,
  minimal,
  minimalMobile,
  status,
  position,
  artistId,
  winnerId,
  disabled = false,
  refetchBattles,
  ...props
}) => {
  const isLoggedIn = useIsLoggedIn();
  const currentBattleSide = battle?.battleSides?.find(
    (battleSide) => battleSide?.artist_id === artistId
  );
  const { data: profileData } = useSelector(
    (state: RootState) => state.profile
  );
  const canPlaceBet = React.useMemo(() => {
    const battleStatus = getBattleStatus(battle);
    const userPositions =
      battle?.battleSides
        ?.reduce(
          (positions, side) => [...positions, ...(side?.positions ?? [])],
          [] as Position[]
        )
        ?.filter((pos) => pos?.user_id === profileData?.id) ?? [];
    const userHasPositionOnOtherArtist = userPositions.some(
      (position) => position?.battle_side_id !== currentBattleSide?.id
    );

    if (
      battle?.type === BattlesType.head2head_battle &&
      !isEmpty(userPositions) &&
      userHasPositionOnOtherArtist
    ) {
      return false;
    }
    return battleStatus === BattleStatus.Open && isLoggedIn;
  }, [battle, isLoggedIn, currentBattleSide, profileData]);
  const claim = React.useMemo(
    () =>
      battle?.claims?.find(
        (claim) => claim.position_id === currentBattleSide?.positions?.[0]?.id
      ),
    [battle?.claims, artistId]
  );
  const hasRewardToClaim =
    [ClaimType.BattleWin, ClaimType.BattleMarketMaker].includes(
      claim?.type as ClaimType
    ) &&
    !isNil(claim) &&
    isNil(claim?.claimed_at);
  const hasClaimedReward =
    [ClaimType.BattleWin, ClaimType.BattleMarketMaker].includes(
      claim?.type as ClaimType
    ) &&
    !isNil(claim) &&
    !isNil(claim?.claimed_at);
  const hasRefundToClaim =
    ClaimType.Refund === claim?.type &&
    !isNil(claim) &&
    isNil(claim?.claimed_at);
  const hasClaimedRefund =
    ClaimType.Refund === claim?.type &&
    !isNil(claim) &&
    !isNil(claim?.claimed_at);

  let content: React.ReactNode = "Buy";
  let classNames =
    "bg-indigo-400/15 text-indigo-400 hover:bg-indigo-600 hover:text-white";

  const getWinContent = () => {
    classNames = "bg-teal-400/15 text-teal-400 pointer-events-none";
    content = minimal ? (
      "Win"
    ) : (
      <>
        <CheckIcon className="size-4" />
        <span className="text-xs">Winner</span>
      </>
    );
  };

  if (status === "active" && !isLoggedIn) {
    return (
      <WithTooltip
        tooltipContent="Sign in required to place a prediction"
        className={cn(
          "rounded-lg bg-slate-700 px-2 py-1 text-xs text-slate-400",
          { "w-full": !minimal }
        )}>
        Buy
      </WithTooltip>
    );
  }

  if (status === "pending") {
    if (minimal) {
      classNames = "hidden opacity-0 pointer-events-none"; // hide
    } else {
      classNames =
        "bg-orange-400/15 pointer-events-none text-orange-400 text-xs";
      content = (
        <>
          <HourglassIcon className="!size-3" />
          <span className="text-[10px]">Waiting for results</span>
        </>
      );
    }
  }

  if (status === "won") {
    if (hasRewardToClaim) {
      classNames = "bg-teal-400 text-xs text-slate-900 hover:bg-teal-400/80";
      content = null;
      return (
        <ClaimButton
          className={cn("py-1.5 text-xs", { "h-min w-full": !minimal })}
          hideIcon={minimal}
          battleId={battle?.onchainId ?? 0}
          battleType={battle?.type}
          claims={[claim]}
          onClaimSuccess={async () => {
            await delay(3000);
            refetchBattles?.();
          }}
        />
      );
    } else if (hasClaimedReward) {
      classNames = "bg-teal-400/15 text-teal-400 pointer-events-none";
      content = minimal ? (
        "Claimed"
      ) : (
        <>
          <CheckIcon className="size-4" />
          <span className="text-xs">Claimed</span>
        </>
      );
    } else if (artistId === winnerId) {
      getWinContent();
    } else {
      if (minimal) {
        return;
      } else {
        classNames = "bg-slate-700 text-slate-400 pointer-events-none";
        content = (
          <>
            <XIcon />
            <span className="text-xs">Defeated</span>
          </>
        );
      }
    }
  }

  if (status === "lost" || status === "completed") {
    if (artistId === winnerId) {
      getWinContent();
    } else {
      if (minimal) {
        return;
      } else {
        classNames = "bg-slate-700 text-slate-400 pointer-events-none";
        content = (
          <>
            <XIcon />
            <span className="text-xs">Defeated</span>
          </>
        );
      }
    }
  }

  if (status === "canceled") {
    if (hasClaimedRefund) {
      classNames = "bg-teal-400/15 text-teal-400 pointer-events-none";
      content = minimal ? (
        "Claimed"
      ) : (
        <>
          <CheckIcon className="size-4" />
          <span className="text-xs">Claimed</span>
        </>
      );
    } else if (hasRefundToClaim) {
      classNames = "bg-orange-400 hover:bg-orange-400/90 text-slate-900";
      content = minimal ? (
        "Refund"
      ) : (
        <>
          <HandCoinsIcon className="size-4" />
          <span className="text-xs">Refund</span>
        </>
      );
    } else {
      if (minimal) {
        return;
      } else {
        classNames = "bg-slate-700 text-slate-400 pointer-events-none";
        content = (
          <>
            <BanIcon />
            <span className="text-xs">Canceled</span>
          </>
        );
      }
    }
  }

  return (
    <Button
      size="sm"
      className={cn("h-min flex-shrink-0 px-2 py-1.5 text-xs", classNames, {
        "w-full": !minimal,
      })}
      disabled={
        disabled || (!canPlaceBet && !hasRefundToClaim && !hasRewardToClaim)
      }
      {...props}>
      {content}
    </Button>
  );
};
